import { v4 } from "uuid";

import {
  CreateExperienceKeyMetricDto,
  ExperienceKeyMetricDto,
} from "@intelligems/ig-types/dtos/experience-key-metrics";
import { CustomEventDto } from "@intelligems/ig-types/dtos/analytics-events/custom-events";
import { ExperienceCustomMetricDto } from "@intelligems/ig-types/dtos/experience-custom-metrics";
import { ExperienceKeyMetricFe } from "@intelligems/ig-types/fe-only/custom-events";
import {
  STANDARD_EVENTS_ALLOWED_KEY_METRICS,
  StandardEventAllowedKeyMetrics,
} from "@intelligems/ig-types/dtos/analytics-events/standard-events";
import * as rtk from "@reduxjs/toolkit";
import { createSelector, PayloadAction } from "@reduxjs/toolkit";
import { initExperienceKeyMetrics } from "@src/api/initials";
import { initExperienceKeyMetricFromStandardEvent } from "@src/features/experienceMetrics/helpers";
import { IExperienceMetricsState } from "@src/store/slices/experienceMetrics/types";
import {
  ensureAndSortOrder,
  filterOutRemovedCustomFromKey,
  filterOutRemovedMetrics,
} from "@src/store/slices/experienceMetrics/utils";
import { sortByOrder } from "@src/utils/sortings";

const sliceInitialMetricsState: IExperienceMetricsState = {
  isSuccess: false,
  keyMetrics: [],
  storedKeyMetrics: [],
  customMetrics: [],
  storedCustomMetrics: [],
};

const experienceMetricsSlice = rtk.createSlice({
  name: "experienceMetrics",
  initialState: sliceInitialMetricsState,
  reducers: {
    initKeyMetrics: (
      draft,
      action: PayloadAction<{
        keyMetrics: ExperienceKeyMetricDto[];
        shouldShowProfit?: boolean;
        hasTestPostPurchase?: boolean;
      }>
    ) => {
      if (action.payload.keyMetrics.length) {
        draft.keyMetrics = action.payload.keyMetrics;
      } else {
        draft.keyMetrics = initExperienceKeyMetrics(
          action.payload.shouldShowProfit ?? false,
          action.payload.hasTestPostPurchase ?? false
        );
      }
      draft.storedKeyMetrics = draft.keyMetrics;
      draft.isSuccess = true;
      return draft;
    },
    initCustomMetrics: (
      draft,
      action: PayloadAction<{
        customMetrics: ExperienceCustomMetricDto[];
      }>
    ) => {
      if (action.payload.customMetrics.length) {
        draft.customMetrics = ensureAndSortOrder(action.payload.customMetrics);
      }
      draft.storedCustomMetrics = draft.customMetrics;
      draft.isSuccess = true;
      return draft;
    },
    reorderKeyMetrics: (
      draft,
      action: PayloadAction<{ startIndex: number; endIndex: number }>
    ) => {
      if (!draft.isSuccess) {
        return draft;
      }
      const [removed] = draft.keyMetrics.splice(action.payload.startIndex, 1);
      draft.keyMetrics.splice(action.payload.endIndex, 0, removed);
      draft.keyMetrics = draft.keyMetrics.map(
        (metric: CreateExperienceKeyMetricDto, index: number) => ({
          ...metric,
          order: index,
        })
      );

      draft.keyMetrics = draft.keyMetrics.sort(sortByOrder);
      return draft;
    },
    reorderCustomMetrics: (
      draft,
      action: PayloadAction<{ startIndex: number; endIndex: number }>
    ) => {
      if (!draft.isSuccess) {
        return draft;
      }
      const [removed] = draft.customMetrics.splice(
        action.payload.startIndex,
        1
      );
      draft.customMetrics.splice(action.payload.endIndex, 0, removed);
      draft.customMetrics = draft.customMetrics.map(
        (metric, index: number) => ({
          ...metric,
          order: index,
        })
      );

      draft.customMetrics = draft.customMetrics.sort(sortByOrder);
      return draft;
    },
    setPrimaryMetric: (
      draft,
      action: PayloadAction<{ currentIndex: number }>
    ) => {
      if (!draft.isSuccess) {
        return draft;
      }
      const { currentIndex } = action.payload;
      const currPrimaryIndex = draft.keyMetrics.findIndex(
        (metric) => metric.isPrimary
      );
      if (currPrimaryIndex !== -1) {
        draft.keyMetrics[currPrimaryIndex].isPrimary = false;
      }

      draft.keyMetrics[currentIndex].isPrimary = true;
      return draft;
    },
    setPrimaryStandardMetric: (
      draft,
      action: PayloadAction<{ standardEventId: string }>
    ) => {
      if (!draft.isSuccess) {
        return draft;
      }
      const targetIndex = draft.keyMetrics.findIndex(
        (metric) => metric.standardEventId === action.payload.standardEventId
      );
      if (targetIndex === -1) {
        return draft;
      }
      const currPrimaryIndex = draft.keyMetrics.findIndex(
        (metric) => metric.isPrimary
      );
      if (currPrimaryIndex !== -1) {
        draft.keyMetrics[currPrimaryIndex].isPrimary = false;
      }
      draft.keyMetrics[targetIndex].isPrimary = true;
      return draft;
    },
    removeKeyMetric: (
      draft,
      action: PayloadAction<{ indexToRemove: number }>
    ) => {
      if (!draft.isSuccess) {
        return draft;
      }
      const { indexToRemove } = action.payload;

      draft.keyMetrics = ensureAndSortOrder(
        draft.keyMetrics.filter((_metric, idx: number) => idx !== indexToRemove)
      );

      if (!draft.keyMetrics.some((metric) => metric.isPrimary)) {
        const indexOfNextEligibleKeyMetric = draft.keyMetrics.findIndex(
          (keyMetric) =>
            keyMetric.standardEventId &&
            STANDARD_EVENTS_ALLOWED_KEY_METRICS[keyMetric.standardEventId]
              .isEligibleForPrimaryMetric
        );
        draft.keyMetrics[indexOfNextEligibleKeyMetric].isPrimary = true;
      }
    },
    removeCustomMetric: (
      draft,
      action: PayloadAction<{ indexToRemove: number }>
    ) => {
      if (!draft.isSuccess) {
        return draft;
      }
      const { indexToRemove } = action.payload;
      draft.keyMetrics = draft.keyMetrics.filter(
        (keyMetric) =>
          keyMetric.experienceCustomMetricId !==
          draft.customMetrics[indexToRemove].id
      );
      draft.keyMetrics = ensureAndSortOrder(draft.keyMetrics);
      draft.customMetrics = ensureAndSortOrder(
        draft.customMetrics.filter(
          (_metric, idx: number) => idx !== indexToRemove
        )
      );
    },
    setKeyMetrics: (
      draft,
      action: PayloadAction<{
        standardEventList: StandardEventAllowedKeyMetrics[];
        selectedCustomEventIds: string[];
        allCustomEvents: CustomEventDto[];
      }>
    ) => {
      if (!draft.isSuccess) {
        return;
      }
      const { standardEventList, selectedCustomEventIds, allCustomEvents } =
        action.payload;

      const netNewStandardEvents = standardEventList.filter(
        (event) =>
          !draft.keyMetrics
            .map((metric) => metric.standardEventId)
            .includes(event)
      );

      // filter out removed key metrics
      const updatedKeyMetrics = filterOutRemovedMetrics(
        draft.keyMetrics,
        draft.customMetrics,
        selectedCustomEventIds,
        standardEventList
      );
      // create net new standard key metrics
      const newStandardKeyMetrics = initExperienceKeyMetricFromStandardEvent(
        netNewStandardEvents,
        updatedKeyMetrics.length - 1
      );
      // add them in
      updatedKeyMetrics.push(...newStandardKeyMetrics);

      // check if in custom metrics, if not add, check if in key metrics, if not add

      selectedCustomEventIds.forEach((customEventId) => {
        const foundCustomMetric = draft.customMetrics.find(
          (customMetric) => customMetric.customEventId === customEventId
        );

        if (!foundCustomMetric) {
          const customEvent = allCustomEvents.find(
            (event) => event.id === customEventId
          );
          // Skip custom events that no longer exist (e.g. deleted after selection)
          // rather than pushing an undefined metric.
          if (!customEvent) {
            return;
          }
          const newCustomMetric = {
            customEventId: customEventId,
            order: draft.customMetrics.length,
            customEvent,
            id: v4(),
          };
          // add custom metric
          draft.customMetrics.push(newCustomMetric);
          // add as key metric
          updatedKeyMetrics.push({
            order: updatedKeyMetrics.length,
            experienceCustomMetricId: newCustomMetric.id,
            isPrimary: false,
          });
        } else {
          const foundCustomKeyMetric = draft.keyMetrics.find(
            (keyMetric) =>
              keyMetric.experienceCustomMetricId === foundCustomMetric.id
          );
          if (!foundCustomKeyMetric) {
            // add as key metric
            updatedKeyMetrics.push({
              isPrimary: false,
              order: updatedKeyMetrics.length,
              experienceCustomMetricId: foundCustomMetric.id,
            });
          }
        }
      });

      draft.keyMetrics = ensureAndSortOrder(updatedKeyMetrics);
    },
    setCustomMetrics: (
      draft,
      action: PayloadAction<{
        selectedCustomEventIds: string[];
        allCustomEvents: CustomEventDto[];
      }>
    ) => {
      if (!draft.isSuccess) {
        return;
      }
      const { selectedCustomEventIds, allCustomEvents } = action.payload;

      // Don't replace existing metrics, just add the new ones that aren't there yet
      // Find which IDs are truly new (not already in the draft.customMetrics)
      const existingEventIds = new Set(
        draft.customMetrics.map((metric) => metric.customEventId)
      );
      const newEventIds = selectedCustomEventIds.filter(
        (id) => !existingEventIds.has(id)
      );

      // Only add the actually new metrics
      newEventIds.forEach((customEventId) => {
        const newCustomMetric = {
          customEventId,
          order: draft.customMetrics.length,
          customEvent: allCustomEvents.find(
            (event) => event.id === customEventId
          )!,
          id: v4(),
        };
        draft.customMetrics.push(newCustomMetric);
      });

      draft.customMetrics = ensureAndSortOrder(draft.customMetrics);

      // Update key metrics
      draft.keyMetrics = ensureAndSortOrder(
        filterOutRemovedCustomFromKey(draft.keyMetrics, draft.customMetrics)
      );
    },
    updateCustomMetricEvent: (
      draft,
      action: PayloadAction<{
        customEvent: CustomEventDto;
      }>
    ) => {
      if (!draft.isSuccess) {
        return;
      }
      const { customEvent } = action.payload;

      draft.customMetrics.forEach((customMetric, index) => {
        if (customMetric.customEventId === customEvent.id) {
          draft.customMetrics[index].customEvent = customEvent;
        }
      });
    },
    resetMetricsToStored: (draft) => {
      if (!draft.isSuccess) {
        return;
      }
      draft.customMetrics = draft.storedCustomMetrics;
      draft.keyMetrics = draft.storedKeyMetrics;
    },
    resetExperienceMetrics: (draft) => {
      draft = sliceInitialMetricsState;
      return draft;
    },
  },
  selectors: {
    getKeyMetrics: (state) => state.keyMetrics,
    getCustomMetrics: (state) => state.customMetrics,
    getMetrics: (state) => state,
  },
});

export const {
  initKeyMetrics,
  reorderKeyMetrics,
  setPrimaryMetric,
  setPrimaryStandardMetric,
  removeKeyMetric,
  setKeyMetrics,
  setCustomMetrics,
  reorderCustomMetrics,
  removeCustomMetric,
  updateCustomMetricEvent,
  initCustomMetrics,
  resetMetricsToStored,
  resetExperienceMetrics,
} = experienceMetricsSlice.actions;

export const experienceMetricsReducer = experienceMetricsSlice.reducer;

export const { getKeyMetrics, getCustomMetrics, getMetrics } =
  experienceMetricsSlice.selectors;

export const getStandardKeyMetricIds = createSelector(
  getKeyMetrics,
  (metrics) =>
    metrics
      .filter(
        (keyMetric) =>
          keyMetric.standardEventId && !keyMetric.experienceCustomMetricId
      )
      .map((metric) => metric.standardEventId as StandardEventAllowedKeyMetrics)
);

// populate select checkboxes in dropdown
export const getCustomMetricIds = createSelector(getCustomMetrics, (metrics) =>
  metrics.map((metric) => metric.customEventId)
);

export const getCustomMetricsMap = createSelector(
  getCustomMetrics,
  (metrics) => {
    return new Map(metrics.map((metric) => [metric.id, metric]));
  }
);

export const getKeyMetricsRich = createSelector(
  getKeyMetrics,
  getCustomMetricsMap,
  (keyMetrics, customMetricsMap): ExperienceKeyMetricFe[] => {
    return keyMetrics
      .map((keyMetric) => ({
        ...keyMetric,
        experienceCustomMetric: customMetricsMap.get(
          keyMetric.experienceCustomMetricId as string
        ),
      }))
      .filter(
        (keyMetric) =>
          keyMetric.standardEventId || keyMetric.experienceCustomMetric
      );
  }
);

export const getCustomKeyMetricEventIds = createSelector(
  getKeyMetricsRich,
  (metrics) =>
    metrics
      .filter(
        (keyMetric) =>
          !keyMetric.standardEventId && keyMetric.experienceCustomMetricId
      )
      .map((metric) => metric.experienceCustomMetric?.customEventId)
      .filter((customEventId): customEventId is string => customEventId != null)
);

export const getPrimaryMetricId = createSelector(
  getKeyMetricsRich,
  (metrics) => {
    const primaryMetric = metrics.find((keyMetric) => keyMetric.isPrimary);
    const fallbackMetric = metrics[0];
    return (
      primaryMetric?.experienceCustomMetric?.customEvent?.identifier ??
      primaryMetric?.standardEventId ??
      fallbackMetric?.experienceCustomMetric?.customEvent?.identifier ??
      fallbackMetric?.standardEventId ??
      ""
    );
  }
);

export const getPrimaryMetricRich = createSelector(
  getKeyMetricsRich,
  (metrics) => {
    const primaryMetric = metrics.find((keyMetric) => keyMetric.isPrimary);
    const fallbackMetric = metrics[0];

    return primaryMetric ?? fallbackMetric;
  }
);
